import React, { useMemo } from 'react';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';

import { formatPurchaseOrderDate } from 'components/procurement/components/purchaseOrdersV2/utils/purchaseOrderDateUtils';
import {
    formatNumber,
    normalizeOptionalText,
} from 'components/procurement/components/purchaseOrdersV2/utils/purchaseOrderDisplayUtils';
import { FC_TRANSFER_DISPLAY_FALLBACK } from 'components/warehouse/components/fcTransfers/fcTransferDisplayUtils';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shadcn/components/ui/Table';

/**
 * @param {Record<string, unknown>} row
 * @returns {string | number}
 */
function formatCases(row) {
    const n = row?.numCases ?? row?.num_cases;
    if (n === undefined || n === null || n === '') return FC_TRANSFER_DISPLAY_FALLBACK;
    return formatNumber(n, { maximumFractionDigits: 0, minimumFractionDigits: 0 });
}

/**
 * @param {Record<string, unknown>} row
 * @returns {string | number}
 */
function formatLoose(row) {
    const n = row?.numLooseUnits ?? row?.num_loose_units;
    if (n === undefined || n === null || n === '') return FC_TRANSFER_DISPLAY_FALLBACK;
    return formatNumber(n, { maximumFractionDigits: 0, minimumFractionDigits: 0 });
}

/**
 * Read-only line items
 *
 * @param {Object} props
 * @param {Array<Record<string, unknown>>} [props.items=[]]
 */
export function FCTransferItemsTableCard({ items = [] }) {
    const tableData = useMemo(() => (Array.isArray(items) ? items : []), [items]);

    const columns = useMemo(
        () => [
            {
                id: 'itemName',
                header: 'Item',
                accessorFn: (row) => row?.itemName ?? row?.item_name,
                cell: ({ getValue }) => normalizeOptionalText(getValue()),
            },
            {
                id: 'externalSku',
                header: 'External SKU',
                accessorFn: (row) => row?.externalSku ?? row?.external_sku,
                cell: ({ getValue }) => normalizeOptionalText(getValue()),
            },
            {
                id: 'lotId',
                header: 'Lot ID',
                accessorFn: (row) => row?.lotId ?? row?.lot_id,
                cell: ({ getValue }) => normalizeOptionalText(getValue()),
            },
            {
                id: 'bbd',
                header: 'BBD',
                accessorFn: (row) => row?.sourceExpiresOnSnapshot ?? row?.source_expires_on_snapshot,
                cell: ({ getValue }) => formatPurchaseOrderDate(getValue(), FC_TRANSFER_DISPLAY_FALLBACK),
            },
            {
                id: 'unitsPerCase',
                header: 'Units / case',
                accessorFn: (row) => row?.sourceUnitsPerCaseSnapshot ?? row?.source_units_per_case_snapshot,
                cell: ({ getValue }) => {
                    const v = getValue();
                    if (v === undefined || v === null || v === '') return FC_TRANSFER_DISPLAY_FALLBACK;
                    return formatNumber(v, { maximumFractionDigits: 2, minimumFractionDigits: 0 });
                },
            },
            {
                id: 'numCases',
                header: 'Quantity (cases)',
                accessorFn: (row) => row,
                cell: ({ getValue }) => formatCases(getValue()),
            },
            {
                id: 'numLooseUnits',
                header: 'Loose units',
                accessorFn: (row) => row,
                cell: ({ getValue }) => formatLoose(getValue()),
            },
            {
                id: 'status',
                header: 'Line status',
                accessorFn: (row) => row?.status,
                cell: ({ getValue }) => normalizeOptionalText(getValue()),
            },
        ],
        [],
    );

    const table = useReactTable({
        data: tableData,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getRowId: (row, index) => String(row?.id ?? `fc-transfer-item-${index}`),
    });

    return (
        <div className="rounded-md border bg-card p-6 shadow-sm">
            <div className="flex flex-col gap-4">
                <h2 className="text-left text-lg font-semibold tracking-tight text-foreground">Line items</h2>
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <TableRow key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => (
                                        <TableHead key={header.id} className="whitespace-nowrap text-left">
                                            {header.isPlaceholder
                                                ? null
                                                : flexRender(header.column.columnDef.header, header.getContext())}
                                        </TableHead>
                                    ))}
                                </TableRow>
                            ))}
                        </TableHeader>
                        <TableBody>
                            {table.getRowModel().rows.length ? (
                                table.getRowModel().rows.map((row) => (
                                    <TableRow key={row.id}>
                                        {row.getVisibleCells().map((cell) => (
                                            <TableCell
                                                key={cell.id}
                                                className="whitespace-normal break-words text-left"
                                            >
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell
                                        colSpan={columns.length}
                                        className="h-24 text-center text-muted-foreground"
                                    >
                                        No line items on this transfer.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>
            </div>
        </div>
    );
}

FCTransferItemsTableCard.displayName = 'FCTransferItemsTableCard';
